/**
 * Security utility functions for input validation and sanitization
 */

// Validate and sanitize text input
export const sanitizeInput = (input: string): string => {
  // Basic sanitization: trim whitespace and remove potentially harmful characters
  return input.trim().replace(/[<>"']/g, '');
};

// Validate IBAN format
export const validateIBAN = (iban: string): boolean => {
  const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/;
  const cleanIban = iban.replace(/\s/g, '').toUpperCase();
  return ibanRegex.test(cleanIban);
};

// Validate SWIFT code format
export const validateSWIFT = (swift: string): boolean => {
  const swiftRegex = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
  const cleanSwift = swift.replace(/\s/g, '').toUpperCase();
  return swiftRegex.test(cleanSwift);
};

// Check for potentially malicious file names
export const isValidFileName = (fileName: string): boolean => {
  // Check for directory traversal attempts
  if (fileName.includes('..') || fileName.includes('/') || fileName.includes('\\')) {
    return false;
  }
  
  // Check for dangerous extensions
  const dangerousExtensions = [
    '.exe', '.bat', '.cmd', '.com', '.pif', '.scr', '.vbs', '.js', '.jar',
    '.php', '.asp', '.jsp', '.py', '.pl', '.sh', '.ps1'
  ];
  
  const lowerFileName = fileName.toLowerCase();
  return !dangerousExtensions.some(ext => lowerFileName.endsWith(ext));
};

// Rate limiting helper
class RateLimiter {
  private attempts: Map<string, { count: number; resetTime: number }> = new Map();
  
  constructor(
    private maxAttempts: number = 5,
    private windowMs: number = 15 * 60 * 1000 // 15 minutes
  ) {}
  
  isAllowed(identifier: string): boolean {
    const now = Date.now();
    const attempt = this.attempts.get(identifier);
    
    if (!attempt || now > attempt.resetTime) {
      this.attempts.set(identifier, { count: 1, resetTime: now + this.windowMs });
      return true;
    }
    
    if (attempt.count >= this.maxAttempts) {
      return false;
    }
    
    attempt.count++;
    return true;
  }
  
  getRemainingTime(identifier: string): number {
    const attempt = this.attempts.get(identifier);
    if (!attempt) return 0;
    
    const remaining = attempt.resetTime - Date.now();
    return Math.max(0, remaining);
  }
}

export const uploadRateLimiter = new RateLimiter(10, 60 * 1000); // 10 uploads per minute

// Generate a secure session ID for tracking submissions
export const generateSessionId = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// Validate environment variables
export const validateEnvironment = (): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!process.env.REACT_APP_SUPABASE_URL) {
    errors.push('REACT_APP_SUPABASE_URL is required');
  }
  
  if (!process.env.REACT_APP_SUPABASE_ANON_KEY) {
    errors.push('REACT_APP_SUPABASE_ANON_KEY is required');
  }
  
  // Admin password is no longer stored as an environment variable in the frontend.
  // Admin authentication is handled by Supabase Auth (server-side) or a separate admin app.
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Content Security Policy headers (for server setup)
export const getCSPHeader = (): string => {
  return [
    "default-src 'self'",
    "script-src 'self' 'unsafe-inline'", // React requires unsafe-inline
    "style-src 'self' 'unsafe-inline'",
    "img-src 'self' data: blob: https:",
    "font-src 'self'",
    "connect-src 'self' https://*.supabase.co wss://*.supabase.co",
    "frame-src 'none'",
    "object-src 'none'"
  ].join('; ');
};

// Log security events (in production, send to monitoring service)
export const logSecurityEvent = (event: string, details: any): void => {
  const timestamp = new Date().toISOString();
  const logEntry = {
    timestamp,
    event,
    details,
    userAgent: navigator.userAgent,
    url: window.location.href
  };
  
  console.warn('Security Event:', logEntry);
  
  // In production, send to monitoring service
  // sendToMonitoring(logEntry);
};